// LIFE · Mountain — the Compass in another view, drawn ON the shared orbit
// backdrop (see orbit.tsx; the home paints the mountain full-bleed). This
// component is only the overlay: the front face's own path with its five
// stops (Endeavour at the foot → Step at the summit), the sector names
// riding around the peak, the D-pad (◀ ▶ next face · ▲ ▼ climb · ❚❚ spin)
// and the sheet for the selected stop.
import React, { useEffect, useMemo, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import type { Sector, Stage } from '../domain/olmps';
import type { OrbCtx } from '../olmps/CompassView';
import { formatLadderRungState } from '../olmps/useTodayData';
import { useSharedTodayData } from '../olmps/TodayDataContext';
import { NodePlacard } from './NodePlacard';
import { DPad } from './DPad';
import type { OracleProject } from '../olmps/useOracleProjects';
import type { OracleSpine } from '../olmps/oracle-spine-data';
import { effectiveAims } from '../olmps/oracle-spine-data';
import type { NowStep } from './useCurrentStep';
import { FACE, SECTORS, type Orbit } from './orbit';
import { DISPLAY_FONT, SECTOR_COLOR, SECTOR_NAME, makeStyles, useV10 } from './theme';

const STAGES = ['Endeavour', 'Goal', 'Aim', 'Today', 'Step'] as const;
type Pt = { x: number; y: number };
// Each face has its OWN path up the mountain (fractions of the square frame).
const STOP_POS_BY_SECTOR: Record<Sector, Pt[]> = {
  S: [{ x: 0.5, y: 0.88 }, { x: 0.38, y: 0.76 }, { x: 0.58, y: 0.64 }, { x: 0.44, y: 0.51 }, { x: 0.5, y: 0.37 }],
  H: [{ x: 0.56, y: 0.88 }, { x: 0.66, y: 0.76 }, { x: 0.56, y: 0.64 }, { x: 0.6, y: 0.5 }, { x: 0.5, y: 0.37 }],
  A: [{ x: 0.64, y: 0.88 }, { x: 0.7, y: 0.74 }, { x: 0.58, y: 0.63 }, { x: 0.62, y: 0.5 }, { x: 0.5, y: 0.37 }],
  P: [{ x: 0.42, y: 0.87 }, { x: 0.54, y: 0.77 }, { x: 0.44, y: 0.66 }, { x: 0.54, y: 0.53 }, { x: 0.5, y: 0.37 }],
  E: [{ x: 0.7, y: 0.86 }, { x: 0.54, y: 0.78 }, { x: 0.66, y: 0.66 }, { x: 0.48, y: 0.54 }, { x: 0.5, y: 0.37 }],
};

export function MountainView({
  orbit,
  frameTop,
  frameSize,
  projects,
  spine,
  current,
  onOpenTool,
  onOrbCtx,
  onExplore,
}: {
  orbit: Orbit;
  /** Top of the square orbit frame, in this view's coordinates. */
  frameTop: number;
  frameSize: number;
  projects: Record<Sector, OracleProject[]>;
  spine: OracleSpine | null;
  current: NowStep | null;
  onOpenTool: (tool: string) => void;
  onOrbCtx: (ctx: OrbCtx) => void;
  /** "Explore ▼" — pan down to the orb on the selected sector/stage. */
  onExplore: () => void;
}) {
  const t = useV10();
  const styles = useStyles();
  const W = frameSize;
  const H = frameSize;
  const [stageIdx, setStageIdx] = useState(4);
  const [placardOn, setPlacardOn] = useState(false);
  const { ladder } = useSharedTodayData();
  const pickStop = (i: number) => {
    setStageIdx(i);
    setPlacardOn(true);
  };
  const { frontSector, frontDeg, settled, spinning } = orbit;

  const node = spine?.[frontSector] ?? null;
  const list = projects[frontSector] ?? [];
  const drift = Math.max(0, ...list.flatMap((p) => p.drift.map((d) => d.score)));
  const driftColor = drift >= 3 ? t.lapsed : drift >= 1 ? t.behind : t.onPace;
  const nextSteps = list.map((p) => p.steps.find((s) => s.status !== 'C')).filter(Boolean) as { text: string }[];
  const stageText: string[] = [
    node?.endeavour || 'No Endeavour set',
    node?.goal ? node.goal.toUpperCase() : 'No Goal set',
    node ? effectiveAims(node).map((a) => a.label).join(' · ') || 'No Aims yet' : 'No Aims yet',
    list.length ? `${list.length} project${list.length === 1 ? '' : 's'} in play · ${drift >= 3 ? 'lapsed' : drift >= 1 ? 'behind pace' : 'on pace'}` : 'Nothing scheduled in this sector today',
    current?.sector === frontSector ? current.title : nextSteps[0]?.text || 'No next step yet',
  ];

  // Sector names ride an ellipse around the peak; a = 0 is the front.
  const markers = useMemo(() => {
    const cx = W * 0.5;
    const cy = H * 0.62;
    const rx = W * 0.45;
    const ry = H * 0.12;
    return SECTORS.map((s, i) => {
      const a = ((i * FACE - frontDeg) * Math.PI) / 180;
      return { s, x: cx + Math.sin(a) * rx, y: cy + Math.cos(a) * ry, depth: (Math.cos(a) + 1) / 2 };
    });
  }, [W, H, frontDeg]);

  const MSTAGE: Stage[] = ['E', 'G', 'A', 'T', 'S'];
  const RUNG = ['endeavour', 'goal', 'aim', 'today', 'step'] as const;
  const rungState = formatLadderRungState(ladder.rungs[RUNG[stageIdx]]);
  useEffect(() => {
    onOrbCtx({ spokeLabel: SECTOR_NAME[frontSector], sector: frontSector, stage: MSTAGE[stageIdx], stageLabel: STAGES[stageIdx], rungState });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frontSector, stageIdx, rungState]);
  const path = STOP_POS_BY_SECTOR[frontSector];
  return (
    <View style={styles.root}>
      <View style={[styles.frame, { top: frameTop, width: W, height: H }]} {...orbit.panHandlers}>
        {markers
          .slice()
          .sort((a, b) => a.depth - b.depth)
          .map((m) => (
            <Pressable key={m.s} onPress={() => orbit.snapToFace(SECTORS.indexOf(m.s))} hitSlop={8} style={[styles.marker, { left: m.x - 60, top: m.y - 14, opacity: m.depth > 0.92 ? 0 : 0.3 + m.depth * 0.7, transform: [{ scale: 0.7 + m.depth * 0.4 }] }]}>
              <Text style={[styles.markerText, { color: SECTOR_COLOR[m.s] }]}>{SECTOR_NAME[m.s].toUpperCase()}</Text>
            </Pressable>
          ))}

        {settled ? (
          <>
            <View pointerEvents="none" style={styles.faceTitleWrap}>
              <Text style={[styles.faceTitle, { color: SECTOR_COLOR[frontSector] }]}>{SECTOR_NAME[frontSector].toUpperCase()} FACE</Text>
            </View>
            {path.map((p, i) => {
              if (i === 0) return null;
              const q = path[i - 1];
              return <View key={`seg-${i}`} pointerEvents="none" style={[styles.seg, { backgroundColor: SECTOR_COLOR[frontSector], boxShadow: `0 0 10px ${SECTOR_COLOR[frontSector]}` } as object, segStyle(q.x * W, q.y * H, p.x * W, p.y * H)]} />;
            })}
            {path.map((p, i) => {
              const on = i === stageIdx;
              return (
                <Pressable key={STAGES[i]} onPress={() => pickStop(i)} hitSlop={10} style={[styles.stop, { left: p.x * W - 13, top: p.y * H - 13 }]}>
                  <View style={[styles.stopDot, on && styles.stopDotOn, on && ({ boxShadow: `0 0 18px ${t.goldGlow}` } as object)]} />
                  <Text style={[styles.stopLabel, on && { color: t.goldBright }]}>{STAGES[i].toUpperCase()}</Text>
                </Pressable>
              );
            })}
          </>
        ) : null}

        <DPad
          style={{ right: 4, top: H * 0.37 + 36 }}
          onUp={() => pickStop(Math.min(4, stageIdx + 1))}
          onDown={() => pickStop(Math.max(0, stageIdx - 1))}
          onLeft={() => {
            setPlacardOn(false);
            orbit.turn(-1);
          }}
          onRight={() => {
            setPlacardOn(false);
            orbit.turn(1);
          }}
          onCenter={orbit.toggleSpin}
          centerGlyph={spinning ? '❚❚' : '▶'}
        />
      </View>

      {placardOn ? (
        <NodePlacard
          kicker={`${SECTOR_NAME[frontSector]} · ${STAGES[stageIdx]}`}
          text={stageText[stageIdx]}
          state={rungState}
          onExplore={() => {
            setPlacardOn(false);
            onExplore();
          }}
        />
      ) : null}
    </View>
  );
}

function segStyle(x1: number, y1: number, x2: number, y2: number) {
  const len = Math.hypot(x2 - x1, y2 - y1);
  const ang = (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI;
  return { left: (x1 + x2) / 2 - len / 2, top: (y1 + y2) / 2, width: len, transform: [{ rotate: `${ang}deg` }] };
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    root: { flex: 1 },
    frame: { position: 'absolute', left: 0 },
    marker: { position: 'absolute', width: 120, alignItems: 'center' },
    markerText: { fontFamily: DISPLAY_FONT, fontSize: 13, letterSpacing: 2, textShadowColor: t.mode === 'dark' ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.9)', textShadowRadius: 6 },
    seg: { position: 'absolute', height: 2.5, opacity: 0.85 } as object,
    faceTitleWrap: { position: 'absolute', top: 4, left: 0, right: 0, alignItems: 'center', gap: 2 },
    faceTitle: { fontFamily: DISPLAY_FONT, fontSize: 18, letterSpacing: 3, textShadowColor: t.mode === 'dark' ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.9)', textShadowRadius: 8 },
    stop: { position: 'absolute', width: 26, alignItems: 'center' },
    stopDot: { width: 14, height: 14, borderRadius: 7, borderWidth: 2, borderColor: t.goldBright, backgroundColor: t.mode === 'dark' ? 'rgba(7,11,23,0.7)' : 'rgba(255,255,255,0.85)', marginTop: 6 },
    stopDotOn: { width: 22, height: 22, borderRadius: 11, backgroundColor: t.gold, marginTop: 2 },
    stopLabel: { color: t.ivory, fontSize: 8.5, fontWeight: '800', letterSpacing: 1.2, marginTop: 3, width: 70, textAlign: 'center', textShadowColor: t.mode === 'dark' ? 'rgba(0,0,0,0.9)' : 'rgba(255,255,255,0.9)', textShadowRadius: 6 },
  }),
);
